import {
	ConfirmationDialog,
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDataListColumn,
	IconButton,
	Id,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
} from "@dgs/core";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFilterQuery } from "../../../../shared/api/filterQueries";
import { useFilter } from "../../../../shared/filters";
import { EventFilterDrawer } from "../../../../shared/filters/events/EventFilterDrawer";
import { FilterBar } from "../../../../shared/filters/FilterBar";
import { EventIndexResource } from "../../../../shared/types/event";
import { eventService } from "../../shared/api/event/eventService";
import { EventExportAction } from "../EventExportAction";
import { useEventActions } from "../useEventActions";
import { useEventListColumns } from "./useEventListColumns";

export const ArchivedEventList: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { restoreEvent, deleteEvent } = useEventActions();
	const [eventIdToRestore, setEventIdToRestore] = useState<Id | null>(null);
	const { activeFilterId, resetFilter, applyFilter, options: filterOptions } = useFilter();
	const { eventListValueColumns } = useEventListColumns({
		hasParticipantStatusColumn: filterOptions.some(({ field }) => field === "EVENT_PARTICIPATION_STATUS"),
	});
	const filterDrawerProps = useEditDrawer();
	const { data: predefinedFilter } = useFilterQuery({ filterId: activeFilterId });
	const openFilterDrawer = useCallback(() => {
		filterDrawerProps.handleOpen(activeFilterId || "new");
	}, [activeFilterId, filterDrawerProps]);

	const archivedEventListColumns: IDataListColumn<EventIndexResource>[] = useMemo(
		() => [
			...eventListValueColumns,
			{
				heading: t("Actions"),
				type: "actions",
				size: "max-content",

				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Restore")}
							label={t("Restore")}
							action={() => setEventIdToRestore(id)}
							icon="unarchive"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => deleteEvent(id)}
							title={t("Delete")}
							label={t("Delete")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this event?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[deleteEvent, eventListValueColumns, t],
	);

	return (
		<>
			<PaginatedAutoDataList
				name="archivedEvents"
				columns={archivedEventListColumns}
				empty={t("No events available")}
				heading={t("Archived Events")}
				headerActions={
					<>
						<IconButton title={t("Open event filter")} onClick={openFilterDrawer} size="small" icon="filter" />
						<EventExportAction filterId={activeFilterId} onExport={eventService.exportArchivedEvents} />
					</>
				}
				headerChildren={<FilterBar filterId={activeFilterId} onRemove={resetFilter} onClick={openFilterDrawer} />}
				listFetchConfig={laravelPaginatedListFetchConfig(
					eventService.filteredArchivedIndexKey,
					(config: PaginatedListFetcherConfig<EventIndexResource>) =>
						eventService.filteredArchivedIndex(config, activeFilterId),
				)}
				onRowClick={(value) => navigate(`${value.id}/details`)}
				initialSearch={{
					search: "",
					searchFields: [
						{ label: t("Start date"), name: "start" },
						{ label: t("End date"), name: "end" },
						{ label: t("Name"), name: "event_translations.name" },
						{ label: t("Project number"), name: "projectNumber" },
						{ label: t("Project owners"), name: "projectOwner" },
						{ label: t("Project managers"), name: "projectManager" },
					],
				}}
			/>
			{filterDrawerProps.editEntityId && (
				<EventFilterDrawer {...filterDrawerProps} filter={predefinedFilter || null} onSubmit={applyFilter} />
			)}
			<ConfirmationDialog
				heading={t("Restore event")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
				open={eventIdToRestore !== null}
				onClose={() => setEventIdToRestore(null)}
				action={() => restoreEvent(eventIdToRestore)}
			>
				{t("Are you sure you want to restore this event?")}
			</ConfirmationDialog>
		</>
	);
};
